import { CaretDown } from '@app/components/icons/caret-down';
import { Menu, Transition } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { Fragment } from 'react';

import { AspectRatio } from '../../domain';
import { Landscape, Portrait } from '../icons';

export type AspectRatioDropdownProps = {
  value: AspectRatio;
  onChange: (value: AspectRatio) => void;
  className?: string;
};

const aspectRatioOptions: {
  id: AspectRatio;
  labelKey: string;
  ratio: string;
  icon: React.ReactNode;
}[] = [
  {
    id: '16_9',
    labelKey: 'content-recording.aspect-ratio.landscape',
    ratio: '16:9',
    icon: <Landscape className="size-5" />,
  },
  {
    id: '9_16',
    labelKey: 'content-recording.aspect-ratio.portrait',
    ratio: '9:16',
    icon: <Portrait className="size-5" />,
  },
];

export const AspectRatioDropdown = ({
  value,
  onChange,
  className,
}: AspectRatioDropdownProps) => {
  const { t } = useTranslation();

  const selectedOption = aspectRatioOptions.find(option => option.id === value);

  return (
    <Menu as="div" className={cx('relative inline-block text-left', className)}>
      {({ open }) => (
        <>
          <Menu.Button
            className={cx(
              'flex items-center gap-2 text-sm font-medium border border-solid border-gray-600 text-spring-neutral-b0 rounded-10 hover:bg-spring-neutral-b0/10 transition-colors',
              'focus:outline-none',
              {
                'bg-spring-neutral-b0/10': open,
              },
            )}
          >
            <div className="flex items-center gap-2 pl-3 pr-2 py-2">
              {selectedOption?.icon}
              <span>
                {t(selectedOption?.labelKey || '')} {selectedOption?.ratio}
              </span>
            </div>
            <div className="pr-2">
              <CaretDown
                className={cx('size-5 transition-transform duration-200', {
                  'rotate-180': open,
                })}
              />
            </div>
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute left-0 mt-2 w-48 origin-top-left rounded-lg bg-spring-neutral-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-600 z-50">
              <div className="p-2">
                {aspectRatioOptions.map(option => (
                  <Menu.Item key={option.id}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => onChange(option.id)}
                        className={cx(
                          'group flex w-full items-center gap-3 rounded-md px-2 py-3 text-sm font-bold text-white hover:bg-spring-neutral-b0/10 transition-colors',
                          {
                            'bg-spring-neutral-b0/10': active,
                          },
                        )}
                      >
                        <span className="text-white">{option.icon}</span>
                        <span className="flex-1 text-left">
                          {t(option.labelKey)}{' '}
                          <span className="text-spring-neutral-b2">
                            {option.ratio}
                          </span>
                        </span>
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};
